import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

interface Playlist {
  id: string
  title: string
  description: string
  spotify_uri: string | null
  cover_color: string
  tags: string[]
}

interface PlaylistsPageProps extends SharedMarketingProps {
  playlists: Playlist[]
}

function PlaylistCard({ playlist }: { playlist: Playlist }) {
  const spotifyEmbedUrl = playlist.spotify_uri
    ? `https://open.spotify.com/embed/playlist/${playlist.spotify_uri}?utm_source=generator&theme=0`
    : null

  return (
    <div className="group rounded-2xl border border-white/8 bg-white/4 p-6 transition-all hover:border-white/16 hover:bg-white/6">
      {spotifyEmbedUrl ? (
        <div className="mb-5 overflow-hidden rounded-xl">
          <iframe
            title={playlist.title}
            src={spotifyEmbedUrl}
            width="100%"
            height="352"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            className="rounded-xl"
            style={{ border: 0 }}
          />
        </div>
      ) : (
        <div
          className="mb-5 flex h-[200px] items-center justify-center rounded-xl"
          style={{ background: `linear-gradient(135deg, ${playlist.cover_color}33, ${playlist.cover_color}11)` }}
        >
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
            </svg>
            <p className="mt-3 text-sm text-white/40">Coming soon</p>
          </div>
        </div>
      )}

      <h3 className="text-lg font-semibold text-white">{playlist.title}</h3>
      <p className="mt-2 text-sm leading-6 text-white/64">{playlist.description}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {playlist.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full px-3 py-1 text-xs font-medium text-white/72"
            style={{ backgroundColor: `${playlist.cover_color}22`, border: `1px solid ${playlist.cover_color}44` }}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  )
}

export default function Playlists({ playlists }: PlaylistsPageProps) {
  return (
    <>
      <AtmosphericHero minHeight="48vh">
        <div className="mx-auto max-w-3xl text-center space-y-10">
          <span className="hero-eyebrow" style={{ justifyContent: 'center' }}>Curated by wavbee</span>
          <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>Our Playlists</h1>
          <p className="hero-body mx-auto">
            Discover the best independent South Indian music — Malayalam, Tamil, and more — hand-picked and updated weekly.
          </p>
        </div>
      </AtmosphericHero>

      {/* Playlist grid */}
      <section className="container mx-auto px-6 py-20">
        <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
          {playlists.map((playlist, index) => (
            <ScrollReveal key={playlist.id} delay={index * 100}>
              <PlaylistCard playlist={playlist} />
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-6 py-24">
        <ScrollReveal>
          <div className="mx-auto max-w-2xl rounded-2xl border border-white/8 bg-white/4 p-10 text-center">
            <h2 className="text-2xl font-bold text-white">Got music that belongs here?</h2>
            <p className="mt-3 text-white/64">
              wavbee artists can submit tracks for playlist consideration. Apply to join and start growing your streams.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href="/apply" className="marketing-button-primary">Apply Now</a>
              <a href="/pricing" className="marketing-button-secondary">See Plans</a>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </>
  )
}
